import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronDown, ChevronRight, Loader2, Plus } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { serviceSuggestionApiService } from '@/lib/api/serviceSuggestion.service';
import { serviceRequestApiService } from '@/lib/api/serviceRequest.service';
import type { ShortlistItem, ServiceRequest } from '@om/shared-types';

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-600' },
  open: { label: 'Open', className: 'bg-blue-100 text-blue-700' },
  quoted: { label: 'Quoted', className: 'bg-purple-100 text-purple-700' },
  under_review: { label: 'Under Review', className: 'bg-amber-100 text-amber-700' },
  closed: { label: 'Closed', className: 'bg-emerald-100 text-emerald-700' },
  reopened: { label: 'Reopened', className: 'bg-yellow-100 text-yellow-700' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-600' },
};

export function ServiceTypeRequestsPage() {
  const { celebrationId, serviceId } = useParams<{ celebrationId: string; serviceId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['services']);
  const cid = Number(celebrationId);
  const sid = Number(serviceId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [serviceItem, setServiceItem] = useState<ShortlistItem | null>(null);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [templateExpanded, setTemplateExpanded] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [summary, reqs] = await Promise.all([
        serviceSuggestionApiService.getShortlist(cid),
        serviceRequestApiService.listRequests(cid, sid).catch((): ServiceRequest[] => []),
      ]);
      const allItems = (summary.by_category ?? []).flatMap(cat => cat.items);
      const found = allItems.find(item => item.service_definition_id === sid) ?? null;
      setServiceItem(found);
      setRequests(reqs);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('services:serviceTypeRequestsPage.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [cid, sid]);

  useEffect(() => { loadData(); }, [loadData]);

  const eventIds = useMemo(() => {
    const ids = new Set<number>();
    for (const r of requests) {
      for (const id of r.applies_to_event_ids ?? []) ids.add(id);
    }
    return ids;
  }, [requests]);
  void eventIds; // may be used for display

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="ml-2 text-sm text-gray-500">{t('services:serviceTypeRequestsPage.loading')}</span>
      </div>
    );
  }

  const serviceName = serviceItem?.name ?? `Service #${sid}`;
  const serviceCategory = serviceItem?.category ?? '';

  return (
    <PageContainer width="wide" className="space-y-5">
      <AppBreadcrumbs />

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{serviceName}</h1>
          {serviceCategory && (
            <p className="mt-0.5 text-sm text-gray-500">
              {t('services:serviceTypeRequestsPage.category', { name: serviceCategory })}
              {serviceItem?.priority && (
                <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${serviceItem.priority === 'must_have' ? 'bg-rose-100 text-rose-700' : 'bg-blue-50 text-blue-600'}`}>
                  {serviceItem.priority === 'must_have' ? t('services:serviceTypeRequestsPage.mustHave') : t('services:serviceTypeRequestsPage.optional')}
                </span>
              )}
            </p>
          )}
          {serviceItem?.tags && serviceItem.tags.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {serviceItem.tags.map(tag => (
                <span key={tag} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/new`)}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          New Service Request
        </button>
      </div>

      {/* Requests */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('services:serviceTypeRequestsPage.serviceRequests', { count: requests.length })}
        </h2>

        {requests.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center">
              <p className="text-sm text-gray-500">{t('services:serviceTypeRequestsPage.noRequests')}</p>
            <button
              type="button"
              onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/new`)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              {t('services:serviceTypeRequestsPage.newRequest')}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map(req => {
              const badge = STATUS_BADGE[req.status] ?? { label: req.status, className: 'bg-gray-100 text-gray-600' };
              const canEdit = ['draft', 'open'].includes(req.status);
              return (
                <div
                  key={req.id}
                  className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{req.request_name}</p>
                        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.className}`}>
                          {badge.label}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-gray-500">
                        {req.budget != null && <span>Budget: ₹{req.budget.toLocaleString()}</span>}
                        {(req.vendor_count ?? 0) > 0 && (
                          <span>{req.vendor_count} vendor{req.vendor_count !== 1 ? 's' : ''}</span>
                        )}
                        {(req.proposal_count ?? 0) > 0 && (
                          <span>{req.proposal_count} proposal{req.proposal_count !== 1 ? 's' : ''}</span>
                        )}
                        <span>{t('services:serviceTypeRequestsPage.created', { date: new Date(req.created_at).toLocaleDateString() })}</span>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/${req.id}/edit`)}
                          className="text-xs font-medium text-gray-600 hover:underline"
                        >
                          {t('services:serviceTypeRequestsPage.edit')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/${req.id}`)}
                        className="inline-flex items-center gap-0.5 text-xs font-medium text-primary hover:underline"
                      >
                          {t('services:serviceTypeRequestsPage.viewDetails')}
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Service Template Info (collapsible) */}
      {serviceItem && (serviceItem.typical_price_range || (serviceItem.custom_fields && serviceItem.custom_fields.length > 0)) && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <button
            type="button"
            onClick={() => setTemplateExpanded(v => !v)}
            className="flex w-full items-center justify-between px-5 py-4 text-sm font-medium text-gray-700"
          >
            {t('services:serviceTypeRequestsPage.serviceTemplateInfo')}
            {templateExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          {templateExpanded && (
            <div className="border-t border-gray-100 px-5 pb-4 pt-3 space-y-2 text-sm text-gray-600">
              {serviceItem.typical_price_range && (
                <p>
                  <span className="font-medium text-gray-700">{t('services:serviceTypeRequestsPage.typicalPriceRange')} </span>
                  {{ low: 'Lower Cost', mid: 'Mid Range', high: 'Premium' }[serviceItem.typical_price_range] ?? serviceItem.typical_price_range}
                </p>
              )}
              {serviceItem.custom_fields && serviceItem.custom_fields.length > 0 && (
                <p>
                  <span className="font-medium text-gray-700">{t('services:serviceTypeRequestsPage.customFields')} </span>
                  {serviceItem.custom_fields.map(f => f.label).join(', ')}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
}
